import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import type { FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import { Table } from "@vilocnv/allsetra-core";
import { Box, useTheme } from "@mui/material";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";
import { STATIONARY_NAME_RENDERING } from "app/data/constants/Reports/stationaryReportsConstants";
import { selectAccountKeysState } from "app/data/selectors";
import {
  buildStationaryReportPayload,
  handleStationaryDateRange,
  stationaryReportsDataFormatter
} from "app/data/helpers/ReportHelpers/stationaryReportHelpers";
import {
  generateReportThunk,
  getAllKeysByAccountThunk,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import StationaryDateExpandable from "components/cards/ReportCards/StationaryDateExpandable/StationaryDateExpandable";
import {
  commonReportsFilterFormValidationSchema,
  customStyles as baseCustomStyles,
  DEFAULT_FILTER_VALUES,
  getStandardizedReportDates,
  reportsCommonFormInitialValues
} from "app/data/helpers/ReportHelpers/commonReportHelpers";

const STATIONARY_REPORT_TYPE = 10;

const customStyles = {
  ...baseCustomStyles,
  headCells: {
    style: {
      ...((
        baseCustomStyles as Record<string, { style?: Record<string, unknown> }>
      )?.headCells?.style || {}),
      justifyContent: "flex-start"
    }
  },
  cells: {
    style: {
      ...((
        baseCustomStyles as Record<string, { style?: Record<string, unknown> }>
      )?.cells?.style || {}),
      justifyContent: "flex-start"
    }
  }
};

const StationaryReports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    accountGroups,
    dateFormat,
    dateValues,
    drawerSelectedAccountId,
    filterOpen,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    report,
    reportsLoading,
    selectedLocalObjects,
    setDateFormat,
    setDateValues,
    setFilterOpen,
    preloadedObjects
  } = useReportCommonStates();

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>(
    DEFAULT_FILTER_VALUES
  );
  const [openModal, setOpenModal] = useState(false);
  const [apiPayload, setApiPayload] = useState(null);
  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const dataSource = report;

  const formattedData = useMemo(
    () => stationaryReportsDataFormatter(dataSource),
    [dataSource]
  );

  const dateColumns = useMemo(() => STATIONARY_NAME_RENDERING(t), [t]);

  const stationaryFiltersSubmitHandler = async (
    values: Record<string, unknown>,
    formikHelpers: FormikHelpers<Record<string, unknown>>
  ) => {
    formikHelpers.setSubmitting(true);
    setFilterValues(values);
    const payload = await buildStationaryReportPayload(
      apiPayload,
      values,
      dateValues
    );
    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = useCallback((value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  }, []);

  const handleDateRangeSubmit = () => {
    const newPayload = handleStationaryDateRange(
      filterValues,
      dateValues,
      apiPayload
    );
    setApiPayload(newPayload);
  };

  const handleLoadReport = () => {
    const newPayload = handleStationaryDateRange(
      filterValues,
      dateValues,
      apiPayload
    );

    dispatch(setReportsFilterData(newPayload));
    dispatch(generateReportThunk(newPayload));
  };

  useEffect(() => {
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.stationaryReport")}
        disabled={reportsLoading}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          handleLoadReport={handleLoadReport}
          disableExportButton={formattedData.length === 0}
          hideDateFormats={true}
          reportType={STATIONARY_REPORT_TYPE}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
        />
        <ReportsCommonFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={stationaryFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={
            selectedLocalObjects.length
              ? selectedLocalObjects
              : preloadedObjects
          }
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          // @ts-expect-error — onChange type mismatch in shared form component
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={reportsCommonFormInitialValues}
          formValidations={commonReportsFilterFormValidationSchema}
          theme={theme}
          size="customSize"
        />
        <Table
          columns={dateColumns}
          data={formattedData}
          progressPending={reportsLoading}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={StationaryDateExpandable}
          pagination={false}
          nestedRowPadding={"16px"}
          translator={t}
          customStyles={customStyles}
        />
      </Box>
    </Box>
  );
};

export default StationaryReports;
